import Gecko from '../gecko/gecko';
import { GECKO_CONSTS, GeckoToastService } from '@geckolabs/gecko-react-ui';
import Utils from '../../Utils';

// Redux
import { overwriteAccounts, addAccounts } from '../../modules/accounts';

// Services
import GeckoStore from '../../services/GeckoStore';

import { Get, Post } from '../../services/ApiRequest';
import { Get as PortalGet, Post as PortalPost } from '../../services/PortalRequest';

const normalizePortalUser = (user) => {
	const firstName = user.firstName ?? user.firstname;
	const lastName = user.lastName ?? user.lastname;

	return {
		id: user.id,
		email: user.email,
		firstName,
		lastName,
		fullName: `${firstName || ''} ${lastName || ''}`.trim() || undefined,
		ssoEnabled: user.ssoEnabled,
		provider: user.provider,
	};
};

const normalizePortalIdp = (idp) => {
	const domains = [
		...(idp.domains || []),
		...(idp.identifiers || []),
		...(idp.IdpIdentifiers || []),
		...(idp.identifier ? [idp.identifier] : []),
		...(idp.domain ? [idp.domain] : []),
	].filter(Boolean);

	return {
		id: idp.id,
		provider: idp.provider ?? idp.providerName ?? idp.name,
		domain: idp.domain ?? idp.identifier ?? domains[0],
		domains,
	};
};

const getEmailDomain = (email) => (email || '').toLowerCase().split('@')[1] || '';

const getIdpDomains = (idps = []) => {
	const domains = new Set();

	idps.forEach((idp) => {
		if (idp.domain) {
			domains.add(String(idp.domain).toLowerCase());
		}

		(idp.domains || []).forEach((domain) => {
			domains.add(String(domain).toLowerCase());
		});
	});

	return domains;
};

const findIdpForEmail = (email, idps = []) => {
	const emailDomain = getEmailDomain(email);

	if (!emailDomain) {
		return null;
	}

	return idps.find((idp) => {
		const idpDomains = [
			idp.domain,
			...(idp.domains || []),
		].filter(Boolean).map((domain) => String(domain).toLowerCase());

		return idpDomains.includes(emailDomain);
	}) || null;
};


export default class AccountLogic {

	/**
	 * Get all accounts
	 *
	 * @param bool persist - should the data be persisted to redux
	 * @return collection of accounts
	 *
	 */
	static get(persist = true, passedParams = {}) {
		let defaultParams = {
			account_rfields: 'created_at,id,name',
			package_rfields: 'title',
			order_by: 'id|ASC',
			page: 1,
			per_page: 10000,
			include: 'package',
		};

		let params = Object.assign({}, defaultParams, passedParams);

		return Get('accounts/', params)
		.then((response) => {
			if(persist) {
        		GeckoStore.redux.dispatch(addAccounts(response.data));
        	}

        	return response;
		});
	}

	buildFeatureToggles(accountPackage = {}, values = false) {
		let data = (values ? {} : []);

        if (accountPackage) {
            let c = 0;

            // Loop round the package object
            for (let prop in accountPackage){

            	// If the key is a feature then add it
                if (prop.indexOf('feature_') !== -1 || prop.indexOf('new_') !== -1) {

                	if(values == false) {
	                    data.push({
	                        key: prop,
	                        label: Utils.capitalize(prop.replace('feature_', '').split('_').join(' '), true),
	                        type: GECKO_CONSTS.FIELD_TYPE_TOGGLE,
	                        width: '16.6%',
	                        break: !c ? true : false,
	                        val: (typeof accountPackage[prop] === 'boolean' ? accountPackage[prop] : false),
	                        searchTags: [prop, 'feature'].join(' '),
	                    });
	                } else {
	                	data[prop] = accountPackage[prop];
	                }

                    c++;
                }
            }
        }

        return data;
    }

    // Limit fields
    buildLimitInputs(accountPackage = {}){
    	let data = [];	

        if (accountPackage) {
            for (let prop in accountPackage){

            	if (prop.indexOf('allowed_') !== -1) {
	                //Add toogle to fields array
	                data.push({
	                	'allowed':{
		                    key: prop + '_allowed',
		                    label: Utils.capitalize(prop.split('_').join(' '), true),
		                    type: GECKO_CONSTS.FIELD_TYPE_NUMBER,
		                    width: '100%',
		                    val: accountPackage[prop].allowed,
		                },
		                'available':{
		                    key: prop + '_available',
		                    label: Utils.capitalize(prop.split('_').join(' '), true),
		                    type: GECKO_CONSTS.FIELD_TYPE_NUMBER,
		                    width: '100%',
		                    val: accountPackage[prop].available,
		                },
		                'used':{
		                    key: prop + '_used',
		                    label: Utils.capitalize(prop.split('_').join(' '), true),
		                    type: GECKO_CONSTS.FIELD_TYPE_NUMBER,
		                    width: '100%',
		                    val: accountPackage[prop].used,
		                },
		                searchTags: [prop, 'limit', 'allowed'].join(' '),
		            });
	            }
            }
        }

        return data;
    }

    static findExport(id) {
    	console.warn('the findExport() method has been deprecated');
    }

    /**
    *
    */
    static addTransaction(data) {
        return Post('/transactions/add-voip-credit', data)
        .then((resp) => {
			if (resp.success) {
				new GeckoToastService().success('Account saved successfully.');
			}
        }).catch((err) => {
            new GeckoToastService().error(err);
        });
    }

	static addToQueue(data) {
		return Post('/' + data.type + 's/add_to_queue', data);
	}

	static markAsFailed(data) {
		return Post('/' + data.type + 's/mark_as_failed', data);
	}

	static getAccountUsers(accountUuid) {
		return PortalGet(`/user/${accountUuid}`).then((response) => {
			const users = Array.isArray(response) ? response : (response.users || response.data || []);

			return users.map(normalizePortalUser);
		});
	}

	static getIdps() {
		return PortalGet('/identity-providers').then((response) => {
			if (response?.success === false) {
				const message = response.errors?.[0]?.message || 'Failed to load identity providers.';
				throw new Error(message);
			}

			const idps = Array.isArray(response)
				? response
				: (response?.data || response?.idps || []);

			return idps.map(normalizePortalIdp);
		});
	}

	static filterSsoEligibleUsers(users = [], idps = []) {
		const domains = getIdpDomains(idps);

		if (!domains.size) {
			return [];
		}

		return users
			.filter((user) => domains.has(getEmailDomain(user.email)))
			.map((user) => {
				if (user.provider) {
					return user;
				}

				const idp = findIdpForEmail(user.email, idps);

				return idp?.provider ? { ...user, provider: idp.provider } : user;
			});
	}

	static getIdentityProviderOptions(idps = []) {
		return idps
			.filter((idp) => idp.id)
			.map((idp) => {
				const domains = [...new Set(
					[idp.domain, ...(idp.domains || [])]
						.filter(Boolean)
						.map((domain) => String(domain).toLowerCase())
				)];
				const domain = domains[0] ?? null;
				const domainLabel = domains.map((entry) => `@${entry}`).join(', ');

				return {
					value: String(idp.id),
					idpId: idp.id,
					label: idp.provider
						? (domainLabel ? `${domainLabel} (${idp.provider})` : idp.provider)
						: (domainLabel || String(idp.id)),
					provider: idp.provider,
					domain,
				};
			});
	}

	static toggleIdentityProviderSsoForUsers(idpId, action) {
		const MAX_BATCHES = 10;

		const requestBatch = (lastEmail, handledSoFar = 0, batchNumber = 1) => {
			const body = { action };

			if (lastEmail) {
				body.lastEmail = lastEmail;
			}

			return PortalPost(`/identity-providers/${idpId}/toggle-sso-for-users`, body).then((response) => {
				if (response?.success === false) {
					const message = response.errors?.[0]?.message || 'Failed to toggle SSO for identity provider.';
					throw new Error(message);
				}

				const data = response?.data || {};
				const processed = data.processed ?? 0;
				const skipped = data.skipped ?? 0;
				const total = data.total ?? 0;
				const nextLastEmail = data.lastEmail ?? data.last;
				const handled = handledSoFar + processed + skipped;

				if (handled < total && nextLastEmail && batchNumber < MAX_BATCHES) {
					return requestBatch(nextLastEmail, handled, batchNumber + 1);
				}

				return response;
			});
		};

		return requestBatch();
	}

	static toggleSsoUser(accountUuid, userId) {
		return PortalPost(`/user/${accountUuid}/${userId}/toggle-sso`).then((response) => {
			if (response.success === false) {
				const message = response.errors?.[0]?.message || 'Failed to toggle SSO.';
				throw new Error(message);
			}

			return normalizePortalUser(response.data || response);
		});
	}
};
